/**
 * Newsletter popup — cookie-consent coordination
 * ---------------------------------------------------------------------------
 * The base <newsletter-popup> (theme.js) extends Maestrooo's Modal, which
 * auto-closes on any click outside its DOM subtree. Shopify renders the cookie
 * banner outside that subtree, so clicking the banner dismisses the newsletter.
 *
 * This module:
 *  1. Suppresses the newsletter auto-show while the GDPR banner is pending,
 *     and re-triggers it (with the configured delay) once the visitor has
 *     accepted/declined cookies.
 *  2. Patches `hideForOutsideClickTarget` on the popup instance so that clicks
 *     on Shopify's cookie banner / preferences dialog don't close it, even if
 *     both surfaces are visible at once.
 *
 * Loaded as `type="module"` from sections/newsletter-popup.liquid so it runs
 * after theme.js has defined and upgraded the <newsletter-popup> element.
 */
(() => {
  class NewsletterPopupConsentGate {
    static SELECTORS = {
      popup: 'newsletter-popup',
      cookieDialogs: '.shopify-pc__banner__dialog, .shopify-pc__prefs__dialog'
    };
    static EVENTS = {
      consentCollected: 'visitorConsentCollected'
    };
    static ATTRS = {
      apparitionDelay: 'apparition-delay'
    };
    static MS_PER_SECOND = 1000;

    static init () {
      const popup = document.querySelector(this.SELECTORS.popup);
      if (!popup) return;

      this._installCookieClickGuard(popup);

      if (this._isConsentResolved()) return;
      this._gateAutoShowUntilConsent(popup);
    }

    static _installCookieClickGuard (popup) {
      const proto = Object.getPrototypeOf(popup);
      const original = proto?.hideForOutsideClickTarget;
      if (typeof original !== 'function') return;

      const cookieSelector = this.SELECTORS.cookieDialogs;
      popup.hideForOutsideClickTarget = function (target) {
        if (target?.closest?.(cookieSelector)) return false;
        return original.call(this, target);
      };
    }

    static _isConsentResolved () {
      const cp = window.Shopify?.customerPrivacy;
      if (!cp || typeof cp.shouldShowGDPRBanner !== 'function') return true;
      try {
        return !cp.shouldShowGDPRBanner();
      } catch (e) {
        return true;
      }
    }

    static _gateAutoShowUntilConsent (popup) {
      const originalShow = popup.show.bind(popup);
      const delayMs = this._readDelayMs(popup);
      let consentReceived = false;

      popup.show = function () {
        if (consentReceived) return originalShow();
      };

      document.addEventListener(this.EVENTS.consentCollected, () => {
        consentReceived = true;
        popup.show = originalShow;
        setTimeout(() => popup.show(), delayMs);
      }, { once: true });
    }

    static _readDelayMs (popup) {
      const raw = popup.getAttribute(this.ATTRS.apparitionDelay);
      const seconds = parseInt(raw, 10);
      return Number.isFinite(seconds) && seconds > 0 ? seconds * this.MS_PER_SECOND : 0;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => NewsletterPopupConsentGate.init(), { once: true });
  } else {
    NewsletterPopupConsentGate.init();
  }
})();
